import { useState, useEffect, useCallback } from 'react';
import { Video, Clock, X, UserCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SessionReminderPopup() {
  const [sessions, setSessions] = useState([]);
  const [dismissed, setDismissed] = useState({});
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/availability/my-upcoming-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.filter(s => s.reminder_level));
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const handleDismiss = (id) => {
    setDismissed(prev => ({ ...prev, [id]: true }));
  };

  const handleJoin = (session) => {
    navigate(`/session/${session.id}`);
  };

  const activeReminders = sessions.filter(s => !dismissed[s.id]);
  if (activeReminders.length === 0) return null;

  const getReminderColor = (level) => {
    switch (level) {
      case 'now': return { bg: 'from-red-600 to-rose-600', border: 'border-red-500/50', text: 'NOW', pulse: true };
      case '15min': return { bg: 'from-amber-600 to-orange-600', border: 'border-amber-500/50', text: 'in 15 min', pulse: true };
      case '30min': return { bg: 'from-amber-500 to-yellow-500', border: 'border-amber-400/50', text: 'in 30 min', pulse: false };
      case '1hr': return { bg: 'from-blue-600 to-indigo-600', border: 'border-blue-500/50', text: 'in 1 hour', pulse: false };
      default: return { bg: 'from-slate-600 to-slate-700', border: 'border-slate-500/50', text: 'upcoming', pulse: false };
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 max-w-sm" data-testid="session-reminder-popup">
      {activeReminders.slice(0, 3).map(session => {
        const style = getReminderColor(session.reminder_level);
        return (
          <div
            key={session.id}
            className={`bg-gradient-to-r ${style.bg} rounded-2xl shadow-2xl border ${style.border} p-4 ${style.pulse ? 'animate-pulse-subtle' : ''}`}
            data-testid={`session-reminder-${session.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Video className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white/80 text-xs font-semibold uppercase tracking-wider">
                    Session {style.text}
                  </span>
                  <button onClick={() => handleDismiss(session.id)} className="text-white/50 hover:text-white transition">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-white font-semibold text-sm mt-1 truncate">
                  {session.subject || session.topic || 'One-on-One Session'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <UserCircle className="w-3 h-3 text-white/60" />
                  <span className="text-white/70 text-xs">{session.other_user?.name || session.teacher_name || 'Participant'}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3 h-3 text-white/60" />
                  <span className="text-white/70 text-xs">{session.date} at {session.start_time}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  {(session.can_join || session.reminder_level === 'now') && (
                    <Button
                      size="sm"
                      className="bg-white text-slate-900 hover:bg-white/90 font-bold text-xs px-4"
                      onClick={() => handleJoin(session)}
                      data-testid={`join-session-btn-${session.id}`}
                    >
                      <Video className="w-3 h-3 mr-1" /> Join Now
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white/70 hover:text-white hover:bg-white/10 text-xs"
                    onClick={() => handleDismiss(session.id)}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.95; transform: scale(1.01); }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
